import React, { useEffect, useState } from 'react';
import { PostRequest } from 'components/common/index.js';

// Throttle the popup so a user who dismisses it isn't nagged on every redraw.
// 4 hours, persisted in localStorage. The banner from _invoice_reminder.html.erb
// is still rendered on every page load so the user has a passive reminder
// outside the popup window.
const DISMISS_HOURS = 4;
const DISMISS_KEY   = 'paymentPastDueDismissedAt';

// Cap manual retries to once per 4 hours per user so a frustrated user can't
// hammer Stripe with repeated failing-card attempts (each one is a real API
// call that Stripe rate-limits and flags). Mirrors the server-side guard in
// charges_controller#retry_invoice.
const RETRY_THROTTLE_HOURS = 4;
const RETRY_KEY            = 'paymentPastDueLastRetryAt';

const PaymentPastDue = ({ translations, modalId }) => {
  const [info, setInfo]         = useState(null);
  const [status, setStatus]     = useState('idle'); // idle | retrying | retry_succeeded | retry_failed
  const [errorMsg, setErrorMsg] = useState('');
  const [retryDisabledUntil, setRetryDisabledUntil] = useState(readLastRetryAt() + RETRY_THROTTLE_HOURS * 60 * 60 * 1000);

  const t = (k) => (translations && translations[k]) || k;

  function readLastRetryAt() {
    try {
      const raw = window.localStorage.getItem(RETRY_KEY);
      if(!raw) { return 0; }
      const at = parseInt(raw, 10);
      return isNaN(at) ? 0 : at;
    } catch (e) { return 0; }
  }

  const retryAllowed = () => Date.now() >= retryDisabledUntil;

  const dismissedRecently = () => {
    try {
      const raw = window.localStorage.getItem(DISMISS_KEY);
      if(!raw) { return false; }
      const at = parseInt(raw, 10);
      if(isNaN(at)) { return false; }
      return (Date.now() - at) < (DISMISS_HOURS * 60 * 60 * 1000);
    } catch (e) {
      return false;
    }
  }

  const showModal = () => {
    const modal = document.getElementById(modalId);
    if(!modal) { return; }
    modal.style.display = "flex";
    if(window.jQuery) { window.jQuery(modal).fadeTo("slow", 1); }
  }

  const hideModal = () => {
    const modal = document.getElementById(modalId);
    if(modal) { modal.style.display = "none"; }
  }

  const handleEvent = (event) => {
    const detail = event && event.detail;
    if(!detail || !detail.past_due) { return; }
    if(dismissedRecently()) { return; }
    setInfo(detail);
    setStatus('idle');
    setErrorMsg('');
    showModal();
    if(typeof trackEvent === 'function') {
      trackEvent('past_due_popup_shown', {
        drawing_id: window.drawing_id,
        card_last4: detail.card_last4
      });
    }
  }

  useEffect(() => {
    const key = 'show_drawing.payment_past_due';
    document.addEventListener(key, handleEvent);
    return () => { document.removeEventListener(key, handleEvent); }
  }, []);

  const handleDismiss = (e) => {
    if(e) { e.preventDefault(); }
    try { window.localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) {}
    if(typeof trackEvent === 'function') {
      trackEvent('past_due_popup_dismissed', { drawing_id: window.drawing_id });
    }
    hideModal();
  }

  const recordRetryTimestamp = () => {
    const now = Date.now();
    try { window.localStorage.setItem(RETRY_KEY, String(now)); } catch (_) {}
    setRetryDisabledUntil(now + RETRY_THROTTLE_HOURS * 60 * 60 * 1000);
  }

  const onRetryFailureResponse = (data) => {
    // Called for any non-2xx response (PostRequest invokes props[status] and
    // then rejects the promise, so this never reaches the success callback).
    recordRetryTimestamp();
    setStatus('retry_failed');
    setErrorMsg((data && data.error) || '');
    if(typeof trackEvent === 'function') {
      trackEvent('past_due_popup_retry_failed', { drawing_id: window.drawing_id, error: (data && data.error) || '' });
    }
  }

  const handleRetry = (e) => {
    e.preventDefault();
    if(!retryAllowed()) { return; }
    setStatus('retrying');
    setErrorMsg('');
    if(typeof trackEvent === 'function') {
      trackEvent('past_due_popup_retry_clicked', { drawing_id: window.drawing_id });
    }
    PostRequest({
      url: '/charges/retry_invoice',
      data: {},
      callback: (data) => {
        recordRetryTimestamp();
        if(data && data.success) {
          setStatus('retry_succeeded');
          if(typeof trackEvent === 'function') {
            trackEvent('past_due_popup_retry_succeeded', { drawing_id: window.drawing_id });
          }
        } else {
          // Server returned 2xx with success: false (e.g. card_declined).
          setStatus('retry_failed');
          setErrorMsg((data && data.error) || '');
          if(typeof trackEvent === 'function') {
            trackEvent('past_due_popup_retry_failed', { drawing_id: window.drawing_id, error: (data && data.error) || '' });
          }
        }
      },
      // Without these handlers, non-2xx responses leave the UI stuck on
      // "Retrying…" forever — PostRequest swallows the rejected promise.
      422: onRetryFailureResponse,
      429: onRetryFailureResponse,
      503: onRetryFailureResponse,
      500: onRetryFailureResponse
    });
  }

  const handleUpdateCard = (e) => {
    e.preventDefault();
    if(!info || !info.stripe_customer_id || !info.stripe_subscription_id) { return; }
    if(typeof trackEvent === 'function') {
      trackEvent('past_due_popup_update_card_clicked', { drawing_id: window.drawing_id });
    }

    // Open the Stripe Checkout session in a new tab so the user's drawing
    // edits aren't lost. We open the tab synchronously (with a placeholder
    // URL) inside the click handler — popup blockers reject window.open()
    // calls that happen inside an async callback, so we have to claim the
    // tab now and rewrite its location once Stripe returns the session URL.
    // noopener/noreferrer breaks window.opener so the Checkout tab can't
    // navigate the originating tab even though Stripe is trusted.
    const newTab = window.open('about:blank', '_blank', 'noopener,noreferrer');
    if(newTab) { newTab.opener = null; }

    PostRequest({
      url: `/charges/create_checkout_session/${info.stripe_customer_id}/${info.stripe_subscription_id}`,
      data: { currency: info.currency },
      callback: (data) => {
        if(data && data.url && newTab) {
          newTab.location.href = data.url;
        } else if(newTab) {
          newTab.close();
        }
      }
    });
  }

  if(!info) { return null; }

  // Brand-coloured mini credit-card so the user instantly recognises which
  // card we're talking about — "a picture is worth a thousand words".
  const cardBrandLabel = info.card_brand ? String(info.card_brand).toUpperCase() : null;
  const cardLast4 = info.card_last4;
  const cardGradient = (brand) => {
    const b = (brand || '').toString().toLowerCase();
    if(b === 'visa')           { return 'linear-gradient(135deg, #1a1f71 0%, #4f5cc7 100%)'; }
    if(b === 'mastercard')     { return 'linear-gradient(135deg, #eb001b 0%, #f79e1b 100%)'; }
    if(b === 'amex' || b === 'american_express') { return 'linear-gradient(135deg, #016fd0 0%, #5eb5e6 100%)'; }
    if(b === 'discover')       { return 'linear-gradient(135deg, #ff6000 0%, #ffaa00 100%)'; }
    return 'linear-gradient(135deg, #2c3e50 0%, #4a6278 100%)';
  }

  const cardVisual = cardBrandLabel && cardLast4 ? (
    <div style={{
      background: cardGradient(info.card_brand),
      color: '#fff',
      borderRadius: 10,
      padding: '18px 22px',
      width: 280,
      margin: '16px auto',
      boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
      fontFamily: '"Courier New", monospace',
      textAlign: 'left'
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 1.5, opacity: 0.9 }}>
        {cardBrandLabel}
      </div>
      <div style={{ fontSize: 22, marginTop: 28, letterSpacing: 3 }}>
        •••• •••• •••• {cardLast4}
      </div>
    </div>
  ) : (
    <p>{t('body_without_card')}</p>
  );

  const nextAttemptLine = info.next_payment_attempt
    ? t('body_next_attempt').replace('%{date}', new Date(info.next_payment_attempt).toLocaleDateString())
    : null;

  return (
    <div className="testimonials-block preview-promo-block" style={{ padding: '24px 16px' }}>
      <h3 style={{ marginTop: 0 }}>
        <i className="fa fa-exclamation-triangle" style={{ color: '#c62828', marginRight: 10 }} aria-hidden="true"></i>
        {t('title')}
      </h3>
      <hr />
      {cardVisual}
      {nextAttemptLine && <p>{nextAttemptLine}</p>}
      <p>{t('subtitle')}</p>

      {status === 'retry_succeeded' && (
        <div className="alert alert-success" role="alert" style={{ marginTop: 12 }}>
          {t('retry_succeeded')}
        </div>
      )}
      {status === 'retry_failed' && (
        <div className="alert alert-danger" role="alert" style={{ marginTop: 12 }}>
          {errorMsg || t('retry_failed_generic')}
        </div>
      )}

      <hr />
      <div style={{ marginTop: 20, display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
        <button className="btn btn-primary" onClick={handleUpdateCard} disabled={status === 'retrying'}>
          {t('update_card')}
        </button>
        {status !== 'retry_succeeded' && (
          <button
            className="btn btn-link"
            onClick={handleRetry}
            disabled={status === 'retrying' || !retryAllowed()}
            title={!retryAllowed() ? t('retry_throttled') : undefined}
            style={{ color: '#888' }}
          >
            {status === 'retrying' ? t('retry_in_progress')
              : !retryAllowed() ? t('retry_throttled')
              : t('retry')}
          </button>
        )}
        <button className="btn btn-link" onClick={handleDismiss}>
          {t('dismiss')}
        </button>
      </div>
    </div>
  );
}

export default PaymentPastDue;
